import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, Award, Zap, Users, Clock, Shield, BadgeCheck, X } from "lucide-react";

function DisclosureModal({ open, onClose, onAccept }) {
  const [agreed, setAgreed] = useState(false);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="disclosure-modal-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        data-testid="disclosure-modal"
        className="relative w-full max-w-2xl bg-[#0d1220] border border-amber-500/30 rounded-xl shadow-2xl shadow-black/50 flex flex-col"
        style={{ maxHeight: "85vh" }}
      >
        {/* Close button */}
        <button
          data-testid="disclosure-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 text-center shrink-0">
          <h2 className="text-xl sm:text-2xl font-extrabold text-amber-400 italic" data-testid="disclosure-title">
            Important Disclosure & Terms of Service
          </h2>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">
            Please review the following information regarding the EasyQuickFund.com application process before proceeding.
          </p>
        </div>

        {/* Scrollable content */}
        <div
          data-testid="disclosure-content"
          className="mx-6 px-5 py-4 overflow-y-auto border border-amber-500/20 rounded-lg bg-[#111827]/60"
          style={{ maxHeight: "40vh" }}
        >
          <div className="space-y-5 text-sm text-slate-300 leading-relaxed">
            <section>
              <h3 className="text-base font-bold text-white mb-2">1. Platform Overview</h3>
              <p>
                EasyQuickFund.com is a consumer access platform that connects individuals with a network of independent third-party lenders. While the platform is commonly used by business partners, it is also available to consumers seeking financing options through our extended lender network.
              </p>
            </section>

            <section>
              <h3 className="text-base font-bold text-white mb-2">2. Credit Check Disclosure</h3>
              <p className="mb-2">
                All applications submitted through EasyQuickFund.com are processed using a soft credit inquiry during the initial review, which does not impact your credit score.
              </p>
              <p>
                If you choose to proceed with a specific lender's offer, the lender may request additional verification, which could include a hard credit inquiry. This would only occur with your authorization.
              </p>
            </section>

            <section>
              <h3 className="text-base font-bold text-white mb-2">3. Platform Service Fee</h3>
              <p className="mb-2">
                EasyQuickFund.com charges a platform service fee for providing access to its lender network, processing applications, and facilitating the evaluation and approval process.
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-slate-400">
                <li>This fee is earned for services performed by the platform, including application processing, lender matching, and administrative support.</li>
                <li>The platform service fee is not interest, is not a finance charge, and is not charged by any lender.</li>
                <li>All loan terms, including rates, fees, and repayment obligations, are determined solely by the lender and may vary.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-base font-bold text-white mb-2">4. Payment Authorization & Timing</h3>
              <p className="mb-2">
                If you choose to proceed after receiving an approval, you authorize EasyQuickFund.com to process the platform service fee.
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-slate-400">
                <li>The fee is processed only after confirmation that loan funds have been successfully delivered.</li>
                <li>Authorization is obtained prior to processing.</li>
                <li>If funding is not completed, no platform service fee is charged.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-base font-bold text-white mb-2">5. ACH Authorization</h3>
              <p className="mb-2">
                By providing your banking information, you authorize EasyQuickFund.com to initiate a one-time electronic debit (ACH) from your designated account for the approved platform service fee, in accordance with the terms outlined above.
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-slate-400">
                <li>This authorization will only be used after confirmation that loan funds have been delivered.</li>
                <li>You certify that you are an authorized user of the bank account provided.</li>
                <li>You understand that this authorization remains in effect until the authorized transaction is completed.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-base font-bold text-white mb-2">6. Acknowledgment of Services</h3>
              <p className="mb-2">
                By proceeding, you acknowledge and agree that the platform service fee is earned upon successful facilitation of funding and confirmation that funds have been delivered.
              </p>
              <p>Once services have been completed, the platform service fee is non-refundable.</p>
            </section>

            <section>
              <h3 className="text-base font-bold text-white mb-2">7. Consent to Contact</h3>
              <p className="mb-2">
                By submitting this application, you authorize EasyQuickFund.com and its lending partners to contact you by phone, email, or SMS (including automated messages) regarding your request, application status, and related services.
              </p>
              <p>
                Message frequency may vary. Message and data rates may apply. You may reply STOP at any time to opt out of SMS messages or reply HELP for assistance.
              </p>
            </section>

            <section>
              <h3 className="text-base font-bold text-white mb-2">8. Important Disclosures</h3>
              <ul className="list-disc list-inside space-y-1.5 text-slate-400">
                <li>EasyQuickFund.com is not a lender or financial institution.</li>
                <li>Approval is not guaranteed.</li>
                <li>Funding timelines vary by lender and applicant qualifications.</li>
                <li>All lending decisions are made by independent third-party lenders.</li>
              </ul>
            </section>
          </div>
        </div>

        {/* Consent + Buttons */}
        <div className="px-6 py-5 shrink-0">
          <h3 className="text-base font-bold text-white mb-3">Acknowledgment and Consent</h3>
          <div className="flex items-start gap-3 mb-5">
            <Checkbox
              id="disclosure-consent"
              data-testid="disclosure-consent-checkbox"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="mt-0.5 border-amber-500/40 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
            />
            <label htmlFor="disclosure-consent" className="text-sm text-slate-400 leading-relaxed cursor-pointer">
              By checking this box, I acknowledge that I have read and understood the Platform Service Fee and the ACH verification process. I agree to these terms and wish to continue with my application.
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Button
              data-testid="disclosure-cancel-btn"
              variant="outline"
              onClick={onClose}
              className="border-slate-600 text-slate-300 hover:bg-slate-800 bg-transparent"
            >
              Cancel
            </Button>
            <Button
              data-testid="disclosure-accept-btn"
              disabled={!agreed}
              onClick={onAccept}
              className="bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-[#0a0e1a] font-bold disabled:opacity-40 cta-gold"
            >
              Continue to Application <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ApplyPage() {
  const navigate = useNavigate();
  const [showDisclosure, setShowDisclosure] = useState(false);

  return (
    <div className="py-14 md:py-20 px-4 bg-apply min-h-screen" data-testid="apply-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3" data-testid="apply-heading">
            Choose Your Application Path
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* LEFT: Partner Application */}
          <Card className="border-slate-700/50 bg-[#111827]/70 backdrop-blur-sm hover:border-amber-500/30 transition-all duration-300 group" data-testid="partner-application-panel">
            <CardContent className="p-8 flex flex-col h-full">
              <div className="flex-1">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-5">
                  <Users className="w-6 h-6 text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-1" data-testid="partner-panel-title">Partner Application</h2>
                <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-4">FOR PTC CLIENT HUB + EASYQUICKFUND PARTNERS</p>
                <p className="text-sm text-slate-400 leading-relaxed mb-6">If you are a current partner with PTC Client Hub or have been referred by an EasyQuickFund representative, select this option.</p>
                <div className="flex flex-wrap gap-2 mb-8">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/8 border border-amber-500/20 text-xs font-medium text-amber-300"><Award className="w-3 h-3" /> Partner Pricing</span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/8 border border-amber-500/20 text-xs font-medium text-amber-300"><Zap className="w-3 h-3" /> Priority Processing</span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/8 border border-amber-500/20 text-xs font-medium text-amber-300"><Users className="w-3 h-3" /> Rep Attribution</span>
                </div>
              </div>
              <Link to="/apply-partner" className="block">
                <Button data-testid="start-partner-application-btn" className="w-full bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-[#0a0e1a] font-bold h-12 text-sm cta-gold">
                  Start Partner Application <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* RIGHT: Consumer Application */}
          <Card className="border-slate-700/50 bg-[#111827]/70 backdrop-blur-sm hover:border-emerald-500/30 transition-all duration-300 group" data-testid="consumer-application-panel">
            <CardContent className="p-8 flex flex-col h-full">
              <div className="flex-1">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-5">
                  <BadgeCheck className="w-6 h-6 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-1" data-testid="consumer-panel-title">Consumer Application</h2>
                <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-4">FOR INDIVIDUAL APPLICANTS</p>
                <p className="text-sm text-slate-400 leading-relaxed mb-6">If you found us online or want to apply directly without a partner referral, select this option for our standard application process.</p>
                <div className="flex flex-wrap gap-2 mb-8">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/8 border border-emerald-500/20 text-xs font-medium text-emerald-300"><Clock className="w-3 h-3" /> Quick Approval</span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/8 border border-emerald-500/20 text-xs font-medium text-emerald-300"><Shield className="w-3 h-3" /> No Credit Impact</span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/8 border border-emerald-500/20 text-xs font-medium text-emerald-300"><BadgeCheck className="w-3 h-3" /> Flexible Terms</span>
                </div>
              </div>
              <Button
                data-testid="start-consumer-application-btn"
                onClick={() => setShowDisclosure(true)}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-400 hover:to-emerald-300 text-[#0a0e1a] font-bold h-12 text-sm cta-gold"
              >
                Start Consumer Application <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Disclosure Modal */}
      <DisclosureModal
        open={showDisclosure}
        onClose={() => setShowDisclosure(false)}
        onAccept={() => navigate("/apply-consumer")}
      />
    </div>
  );
}
